"use client";
import * as React from "react";
import { Box, Button, Divider, Menu, MenuItem } from "@mui/material";
import { ChevronDown, FolderOpen, Plus } from "lucide-react";
import { neuTriggerLabelSx, neuTriggerSx } from "./motion";
import { usePortalScope } from "@/analytics/PortalScope";

export interface ProjectChoice {
  id: string;
  name: string;
}

export interface ProjectBadgeProps {
  sessionId: string;
  currentProjectId: string | null;
  availableProjects: ProjectChoice[];
  onMove: (projectId: string | null) => Promise<void>;
  onCreateProject?: () => void;
}

export function ProjectBadge({
  currentProjectId,
  availableProjects,
  onMove,
  onCreateProject,
}: ProjectBadgeProps) {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  // Shown only in the composer (ComposerProjectBadge): the portaled menu
  // repeats its trigger's section path, composer-project.
  const menuScope = usePortalScope("composer-project");
  const current = availableProjects.find((p) => p.id === currentProjectId);
  const label = current ? current.name : "No Projects Selected";

  return (
    <>
      <Button
        size="small"
        data-ph="project-open"
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        startIcon={<FolderOpen size={15} />}
        endIcon={<ChevronDown size={15} />}
        sx={neuTriggerSx(Boolean(anchor))}
        title={label}
      >
        <Box component="span" sx={neuTriggerLabelSx}>{label}</Box>
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ list: menuScope }}
      >
       {!currentProjectId && onCreateProject && (
         <MenuItem
           data-ph="create"
           onClick={() => {
             setAnchor(null);
             onCreateProject();
           }}
         >
           <Plus size={15} style={{ marginRight: 8 }} />
           Create project
         </MenuItem>
       )}
       {!currentProjectId && availableProjects.length > 0 && onCreateProject && <Divider />}
       {availableProjects.map((p) => (
         <MenuItem
           key={p.id}
           data-ph="assign"
           onClick={async () => {
             await onMove(p.id);
             setAnchor(null);
           }}
         >
           {p.name}
         </MenuItem>
       ))}
       {currentProjectId && <Divider />}
       {currentProjectId && (
         <MenuItem
            data-ph="assign_none"
            onClick={async () => {
              await onMove(null);
              setAnchor(null);
            }}
          >
            Remove from project
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
